import type { SetupOrigin } from '@mogzo/shared';
import { useState } from 'react';
import { ApiError, apiSend, type Project } from '../lib/api';
import { timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';
import { AchievementForm, type AchievementSettings } from './AchievementForm';
import { CliTag, MadeFrom, touchedByCli } from './MadeFrom';
import { Alert, Button, cardClass, ErrorNotice, Loading } from './ui';

type Env = 'test' | 'prod';

interface Achievement extends AchievementSettings {
  unlocked: number;
  inProgress: number;
  origin: SetupOrigin;
}
interface Player {
  playerId: string;
  name: string | null;
}
interface Players {
  unlocked: { player: Player; unlockedAt: string }[];
  inProgress: { player: Player; progress: number }[];
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en')} ${n === 1 ? one : many}`;
const percent = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

function Icon({ url, size = 'md' }: { url: string | null; size?: 'md' | 'lg' }) {
  const box = size === 'lg' ? 'size-14 rounded-[14px]' : 'size-10 rounded-[10px]';
  return url ? (
    <img src={url} alt="" className={`${box} shrink-0`} />
  ) : (
    <span className={`${box} shrink-0 border border-dashed border-line`} />
  );
}

function PlayerName({ player }: { player: Player }) {
  return (
    <>
      <strong>{player.name ?? `Player ${player.playerId}`}</strong>
      <br />
      <span className="font-mono text-[13px] text-muted">{player.playerId}</span>
    </>
  );
}

/** Test only: who has it and who is on the way, with Reset so a tester can earn it again. */
function TestPlayers({
  project,
  achievement,
  refresh,
  onChange,
}: {
  project: Project;
  achievement: Achievement;
  refresh: number;
  onChange: () => void;
}) {
  const base = `/v1/dev/projects/${project.id}/achievements/${achievement.id}/players`;
  const players = useApi<Players>(base, refresh);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reset(player: Player) {
    setBusy(player.playerId);
    setError(null);
    try {
      await apiSend('DELETE', `${base}/${encodeURIComponent(player.playerId)}`);
      onChange();
    } catch (failed) {
      setError(failure(failed));
    }
    setBusy(null);
  }

  if (players.status === 'loading') return <Loading />;
  if (players.status === 'error') return <ErrorNotice error={players.error} />;
  const { unlocked, inProgress } = players.data;
  const { steps } = achievement;
  const who = (player: Player) => player.name ?? `Player ${player.playerId}`;
  return (
    <>
      {error && <Alert tone="error">{error}</Alert>}
      {unlocked.length === 0 ? (
        <p className="px-1 py-2 text-sm text-muted">No tester has unlocked it yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="border-b border-divider px-3 py-2.5">Player</th>
                <th className="border-b border-divider px-3 py-2.5">Unlocked</th>
                <th className="border-b border-divider px-3 py-2.5">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {unlocked.map(({ player, unlockedAt }) => (
                <tr key={player.playerId}>
                  <td className="border-b border-divider px-3 py-3">
                    <PlayerName player={player} />
                  </td>
                  <td className="border-b border-divider px-3 py-3 text-muted">
                    {timeAgo(unlockedAt)}
                  </td>
                  <td className="border-b border-divider px-3 py-3 text-right">
                    <Button
                      variant="secondary"
                      className="min-h-10 px-3.5"
                      aria-label={`Reset ${who(player)}’s unlock`}
                      disabled={busy === player.playerId}
                      onClick={() => reset(player)}
                    >
                      Reset
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-1 text-[13px] text-muted">
        Reset takes it back from that tester, so they can earn it again and you can see it unlock.
        Test data only.
      </p>
      {steps && inProgress.length > 0 && (
        <div className="flex flex-col gap-2 px-1">
          <h3 className="font-semibold">In progress</h3>
          <ul className="flex flex-col gap-2.5">
            {inProgress.map(({ player, progress }) => (
              <li key={player.playerId} className="flex flex-wrap items-center gap-3">
                <span className="min-w-40">
                  <PlayerName player={player} />
                </span>
                <span
                  className="h-2 min-w-32 flex-1 overflow-hidden rounded-full bg-field"
                  aria-hidden="true"
                >
                  <span
                    className="block h-full rounded-full bg-brand"
                    style={{ width: `${percent(progress, steps)}%` }}
                  />
                </span>
                <span className="text-sm font-semibold">
                  {progress.toLocaleString('en')} / {steps.toLocaleString('en')}
                </span>
                <Button
                  variant="secondary"
                  className="min-h-10 px-3.5"
                  aria-label={`Reset ${who(player)}’s progress`}
                  disabled={busy === player.playerId}
                  onClick={() => reset(player)}
                >
                  Reset
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** The Achievements tab of the data browser (M18), from the approved mockup. */
export function Achievements({
  project,
  env,
  refresh,
}: {
  project: Project;
  env: Env;
  refresh: number;
}) {
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<'new' | Achievement | null>(null);
  const base = `/v1/dev/projects/${project.id}/achievements`;
  const state = useApi<{ players: number; achievements: Achievement[] }>(
    `${base}?env=${env}`,
    reload + refresh,
  );

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'error') return <ErrorNotice error={state.error} />;
  const { players, achievements: list } = state.data;
  const current = list.find((a) => a.id === selected) ?? list[0] ?? null;
  const total = list.reduce((sum, a) => sum + a.points, 0);
  const fromCli = list.filter((a) => touchedByCli(a.origin)).length;
  const counts = (a: Achievement) =>
    [
      plural(a.points, 'point'),
      a.steps ? plural(a.steps, 'step') : null,
      `${a.unlocked.toLocaleString('en')} of ${plural(players, 'player')}`,
    ]
      .filter(Boolean)
      .join(' · ');

  if (editing) {
    return (
      <AchievementForm
        project={project}
        achievement={editing === 'new' ? undefined : editing}
        onCancel={() => setEditing(null)}
        onDone={(id) => {
          setEditing(null);
          setSelected(id);
          setReload((n) => n + 1);
        }}
      />
    );
  }
  if (!current) {
    return (
      <section className={`${cardClass} flex flex-col items-start gap-3 p-6`}>
        <h2 className="text-[15px] font-semibold">No achievements yet</h2>
        {env === 'test' ? (
          <>
            <p className="text-muted">
              Make one here, then unlock it from your game with{' '}
              <span className="font-mono">mogzo.achievements.unlock</span>.
            </p>
            <Button variant="brand" onClick={() => setEditing('new')}>
              New achievement
            </Button>
          </>
        ) : (
          <p className="text-muted">
            Live achievements come from your last published version, once you publish one.
          </p>
        )}
      </section>
    );
  }

  if (env === 'prod') {
    return (
      <section
        aria-labelledby="live-achievements-heading"
        className={`${cardClass} flex flex-col gap-4 p-5`}
      >
        <div>
          <h2 id="live-achievements-heading" className="text-[15px] font-semibold">
            How many players have each
          </h2>
          <p className="mt-1 text-sm text-muted">
            Of {plural(players, 'player')} who played since publishing
          </p>
        </div>
        <ul className="flex flex-col gap-3">
          {list.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3">
              <Icon url={a.iconUrl} />
              <span className="min-w-44 flex-1">
                <strong>{a.name}</strong>
                {a.hidden && <span className="ml-2 text-[13px] text-muted">Hidden</span>}
                <br />
                <span className="text-[13px] text-muted">
                  {plural(a.points, 'point')}
                  {a.steps ? ` · ${plural(a.steps, 'step')}` : ''}
                </span>
              </span>
              <span
                className="h-2 min-w-32 flex-1 overflow-hidden rounded-full bg-field"
                aria-hidden="true"
              >
                <span
                  className="block h-full rounded-full bg-brand"
                  style={{ width: `${percent(a.unlocked, players)}%` }}
                />
              </span>
              <span className="min-w-24 text-right text-sm">
                <strong>{a.unlocked.toLocaleString('en')}</strong> · {percent(a.unlocked, players)}%
              </span>
            </li>
          ))}
        </ul>
        <p className="text-[13px] text-muted">
          A low number can mean a hard achievement, or one that’s broken. These come from your last
          published version; new and changed achievements reach live players when you publish. Live
          unlocks can’t be reset here.
        </p>
      </section>
    );
  }

  return (
    <div className="flex flex-wrap items-start gap-5">
      <section
        aria-labelledby="achievements-heading"
        className={`${cardClass} flex max-w-[340px] flex-[1_1_260px] flex-col gap-3 p-4`}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 id="achievements-heading" className="text-[15px] font-semibold">
            Achievements
          </h2>
          <Button variant="brand" className="min-h-10 px-4" onClick={() => setEditing('new')}>
            New achievement
          </Button>
        </div>
        <p className="text-sm text-muted">
          Made here. {plural(list.length, 'achievement')} · {plural(total, 'point')} · of{' '}
          {plural(players, 'player')} who played. Live players get changes when you publish a
          version.
        </p>
        {fromCli > 0 && (
          <p className="text-sm text-muted">
            {fromCli === 1 ? '1 came' : `${fromCli} came`} from the CLI, by you or an AI tool you
            use. They work like the rest: change or delete them here any time.
          </p>
        )}
        <ul className="flex flex-col gap-1">
          {list.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                aria-pressed={a.id === current.id}
                onClick={() => setSelected(a.id)}
                className={`flex w-full items-center gap-3 rounded-[6px] border px-3 py-2.5 text-left ${
                  a.id === current.id
                    ? 'border-brand bg-cream'
                    : 'border-transparent hover:border-line'
                }`}
              >
                <Icon url={a.iconUrl} />
                <span className="min-w-0">
                  <strong>
                    {a.name}
                    {touchedByCli(a.origin) && <CliTag />}
                  </strong>
                  {a.hidden && <span className="ml-2 text-[13px] text-muted">Hidden</span>}
                  <br />
                  <span className="text-[13px] text-muted">{counts(a)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section
        aria-labelledby="achievement-heading"
        className={`${cardClass} flex min-w-0 flex-[999_1_440px] flex-col gap-3 p-4`}
      >
        <div className="flex flex-wrap items-start gap-3 px-1">
          <Icon url={current.iconUrl} size="lg" />
          <div className="min-w-0 flex-1">
            <h2 id="achievement-heading" className="text-[15px] font-semibold">
              {current.name}
            </h2>
            <p className="text-[13px] text-muted">
              <span className="font-mono">{current.id}</span> · {plural(current.points, 'point')}
              {current.steps ? ` · ${plural(current.steps, 'step')}` : ''} ·{' '}
              {current.hidden ? 'hidden until earned' : 'shown to everyone'}
            </p>
            {current.description && <p className="mt-1.5">{current.description}</p>}
            <MadeFrom origin={current.origin} />
          </div>
          <Button variant="secondary" className="min-h-10 px-4" onClick={() => setEditing(current)}>
            Edit
          </Button>
        </div>
        <TestPlayers
          key={current.id}
          project={project}
          achievement={current}
          refresh={reload + refresh}
          onChange={() => setReload((n) => n + 1)}
        />
      </section>
    </div>
  );
}
